/**
 * Contract for the `publishforumreply` callable, imported by both the handler
 * and the reply editors so the two sides cannot drift.
 *
 * The callable moderates a reply's draft body, copies it to the published
 * TipTap doc, and sets the reply's publish and moderation fields. Clients may
 * not write those fields themselves.
 */

import {z} from 'zod';

/** Deployed name of the callable. */
export const PUBLISH_FORUM_REPLY_FUNCTION_NAME = 'publishforumreply';

/** Matches a single Firestore path segment, so an id never addresses a nested path. */
export const ForumPathSegmentSchema = z
  .string()
  .min(1)
  .max(200)
  .refine(value => !value.includes('/'), 'must not contain "/"');

/** Request body of `publishforumreply`. */
export const PublishForumReplyRequestSchema = z.object({
  /** The course the forum belongs to. */
  courseId: ForumPathSegmentSchema,
  /** The forum the reply belongs to. */
  forumId: ForumPathSegmentSchema,
  /** The reply to publish, or to republish after an edit. */
  replyId: ForumPathSegmentSchema,
});

/** Parsed request body of `publishforumreply`. */
export type PublishForumReplyRequest = z.infer<
  typeof PublishForumReplyRequestSchema
>;

/** Response body of `publishforumreply`. */
export interface PublishForumReplyResponse {
  /**
   * Whether the reply is flagged after this publish, by moderation or by an
   * earlier staff flag. A flagged reply is hidden from students and sends no
   * notifications.
   */
  isFlagged: boolean;
}
